import { ContextType, ConversationParticipantRole, ConversationSummaryDTO, ConversationType } from 'models/conversations'

/** Minimal translate-function shape, to avoid coupling helpers to i18next's generic `TFunction`. */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string

const ROLE_LABEL_KEY: Record<ConversationParticipantRole, string> = {
  [ConversationParticipantRole.CLIENT]: 'chat:role.client',
  [ConversationParticipantRole.CREATIVE]: 'chat:role.creative',
  [ConversationParticipantRole.AGENT]: 'chat:role.agent',
}

/**
 * Picks the most descriptive business-context label of a conversation (the order it's about, else any
 * labelled context such as the property). This is what distinguishes multiple conversations between the
 * same two people from one another.
 */
const getContextLabel = (conversation: ConversationSummaryDTO): string | undefined => {
  const contexts = conversation.contexts ?? []
  const orderContext = contexts.find((context) => context.type === ContextType.ORDER)
  return (orderContext ?? contexts.find((context) => !!context.label))?.label
}

/**
 * Resolves the title shown for a conversation in the list.
 *
 * Falls back gracefully, most-specific first: an explicit `title`, the AI-set `subject`, the assistant
 * label for AI conversations, the support label for Ops conversations, the business context (order
 * #/property — so multiple conversations with the same person stay distinguishable), then the other
 * participant's role label, and finally "untitled".
 */
export const getConversationDisplayTitle = (
  conversation: ConversationSummaryDTO,
  currentUserId: string | undefined,
  t: TranslateFn
): string => {
  if (conversation.title) return conversation.title

  if (conversation.subject) return conversation.subject

  if (conversation.type === ConversationType.AI_SUPPORT) {
    return t('chat:conversation.assistant_title')
  }

  // A support conversation carries no counterparty until an Ops agent joins via Slack, so the role-label
  // fallback below would render it as "Conversation" and then as "Agent". Name it for what it is instead.
  if (conversation.type === ConversationType.AGENT_CLIENT) {
    return t('chat:conversation.support_title')
  }

  const contextLabel = getContextLabel(conversation)
  if (contextLabel) return contextLabel

  const otherParticipant = conversation.participants.find((participant) => participant.userId !== currentUserId)
  if (otherParticipant) {
    return t(ROLE_LABEL_KEY[otherParticipant.role])
  }

  return t('chat:conversation.untitled')
}

/** Title of an open thread: who the conversation is with, never the first question the list shows as its title. */
export const getThreadHeaderTitle = (
  conversation: ConversationSummaryDTO,
  currentUserId: string | undefined,
  t: TranslateFn
): string => getConversationDisplayTitle({ ...conversation, title: undefined }, currentUserId, t)

export const distinctLabels = (labels: Array<string | undefined>): string[] =>
  labels.filter((label, index, all): label is string => !!label && all.indexOf(label) === index)
